import { Portal } from 'components/Basic/Portal/Portal';
import { UserConsentContainer } from 'components/Blocks/UserConsent/UserConsentContainer';
import { Error503 } from 'components/Pages/ErrorPage/503/Error503';
import { SocialLoginDataContext } from 'context/SocialLoginDataProvider/SocialLoginDataProvider';
import { UserConsentContext } from 'context/UserConsentProvider/UserConsentProvider';
import { getCookie } from 'cookies-next';
import { ServerSidePropsType } from 'helpers/InitServerSideProps';
import { useAuthLoader } from 'hooks/app/useAuthLoader';
import { usePageLoader } from 'hooks/app/usePageLoader';
import { usePersistStoreHydration } from 'hooks/app/useStoreHydration';
import { useReloadCart } from 'hooks/cart/useReloadCart';
import { useSetGeneralSlice } from 'hooks/setGeneralSlice';
import { useBroadcastChannel } from 'hooks/useBroadcastChannel';
import { useAfterUserEntry } from 'hooks/user/useAfterUserEntry';
import { useSetUserSlice } from 'hooks/useSetUserSlice';
import { PersooWatchDog } from 'modules/persoo/PersooWatchDog';
import { usePersooAddToCart } from 'modules/persoo/usePersooAddToCart';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { PropsWithChildren, useEffect } from 'react';
import { usePersistStore } from 'store/usePersistStore';
import { useSessionStore } from 'store/useSessionStore';
import { GtmProvider } from 'utils/Gtm/context/GtmProvider';

type PageContentProviderProps = PropsWithChildren<{
    pageProps: ServerSidePropsType;
}>;

const AddToCartPopup = dynamic(
    () =>
        import('components/Blocks/Product/AddToCartPopup/AddToCartPopup').then((component) => component.AddToCartPopup),
    {
        ssr: false,
    },
);

const PageContentProvider: FC<PageContentProviderProps> = ({ children, pageProps }) => {
    usePersistStoreHydration();
    useAuthLoader();
    usePersooAddToCart();
    useReloadCart();
    usePageLoader();
    useAfterUserEntry();

    const updateUserState = usePersistStore((s) => s.updateUserState);
    const addToCartPopupData = usePersistStore((s) => s.addToCartPopupData);
    const addToCartFetching = usePersistStore((s) => s.addToCartFetching);
    const updatePortalContent = useSessionStore((s) => s.updatePortalContent);
    const router = useRouter();

    const {
        defaultStoreUuid,
        cartUuid,
        lastVisitedProductsCatNumbers,
        userConsentCookie,
        socialLoginData,
        isMaintenance,
    } = pageProps;

    useSetUserSlice({ defaultStoreUuid: defaultStoreUuid, cartUuid: cartUuid });
    useSetGeneralSlice({ lastVisitedProductsCatNumbers: lastVisitedProductsCatNumbers ?? undefined });

    useBroadcastChannel('reloadPage', () => {
        router.reload();
    });

    useEffect(() => {
        if (!addToCartPopupData) {
            return;
        }
        updatePortalContent(
            <AddToCartPopup
                addedAsVirtualSet={addToCartPopupData.addedAsVirtualSet}
                fetching={addToCartFetching}
                gtmListName={addToCartPopupData.gtmListName}
                listIndex={addToCartPopupData.listIndex}
                product={addToCartPopupData.product}
                virtualSetTotalProductsCount={addToCartPopupData.virtualSetTotalProductsCount}
                virtualSetTotalProductsQuantity={addToCartPopupData.virtualSetTotalProductsQuantity}
                onCloseCallback={() => updateUserState({ addToCartPopupData: null })}
            />,
        );
    }, [addToCartFetching, addToCartPopupData, updatePortalContent, updateUserState]);

    const userConsentFromCookies = getCookie('userConsent');
    const userConsentFromCookiesObject =
        userConsentFromCookies !== undefined ? JSON.parse(decodeURIComponent(userConsentFromCookies)) : null;
    const userConsent = userConsentFromCookiesObject ?? userConsentCookie;

    return (
        <GtmProvider>
            <UserConsentContext.Provider value={userConsent}>
                <SocialLoginDataContext.Provider value={socialLoginData}>
                    {userConsent === null && <UserConsentContainer />}
                    {isMaintenance ? <Error503 /> : children}
                </SocialLoginDataContext.Provider>
            </UserConsentContext.Provider>
            <PersooWatchDog />
            <Portal />
        </GtmProvider>
    );
};

export default PageContentProvider;
